import React from "react";
import { Link, useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

import AddBusinessOutlinedIcon from "@mui/icons-material/AddBusinessOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";

import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import MDButton from "mdComponents/MDButton";

import BasicLayoutNew from "layouts/authentication/components/BasicLayoutNew";
import { MainLogo } from "components/Icons/MainLogo";
import { APP_NAME } from "constants";
import bgImage from "assets/images/city.jpg";
import {
  authFeatureChipSx,
  authPanelSx,
  authSurfaceSx,
  primaryButtonSx,
  secondaryButtonSx,
} from "../authStyles";

const steps = [
  "Create your business account",
  "Confirm your email address",
  "Add your location details",
  "Manage access, ratings, and visits",
];

function SignUp() {
  const navigate = useNavigate();

  return (
    <BasicLayoutNew image={bgImage} wide>
      <Grid container spacing={3} alignItems="stretch" justifyContent="center">
        <Grid item xs={12} lg={5}>
          <Card sx={{ ...authSurfaceSx, height: "100%" }}>
            <MDBox p={{ xs: 3, md: 4 }}>
              <MDBox textAlign="center">
                <MDBox
                  sx={{
                    minHeight: 116,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <MainLogo style={{ width: "220px", maxWidth: "78%" }} />
                </MDBox>
                <MDTypography variant="h3" fontWeight={900} color="dark" mt={1}>
                  Host with {APP_NAME}
                </MDTypography>
                <MDTypography variant="body2" color="text" fontWeight="medium" mt={1.25}>
                  Build your host profile and start turning restroom access into real
                  business visibility.
                </MDTypography>
              </MDBox>

              <MDBox mt={3}>
                <MDButton
                  variant="gradient"
                  color="info"
                  fullWidth
                  onClick={() => navigate("/authentication/sign-up-form")}
                  sx={primaryButtonSx}
                  startIcon={<ArrowForwardOutlinedIcon />}
                >
                  Get Started
                </MDButton>
                <MDBox mt={1.25}>
                  <MDButton
                    variant="outlined"
                    color="dark"
                    fullWidth
                    onClick={() => navigate("/authentication/sign-in")}
                    sx={secondaryButtonSx}
                    startIcon={<ArrowBackOutlinedIcon />}
                  >
                    Back to Login
                  </MDButton>
                </MDBox>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Card sx={{ ...authPanelSx, height: "100%" }}>
            <MDBox p={{ xs: 3, md: 5 }}>
              <MDBox sx={authFeatureChipSx}>
                <AddBusinessOutlinedIcon sx={{ fontSize: 18 }} />
                Business setup
              </MDBox>
              <MDTypography
                variant="h2"
                fontWeight={900}
                mt={2}
                mb={1.25}
                sx={{ color: "#ffffff", lineHeight: 1.08 }}
              >
                Register your business in four quick steps.
              </MDTypography>
              <MDTypography
                variant="body1"
                sx={{ color: "rgba(248, 251, 255, 0.78)", fontWeight: 500, lineHeight: 1.7 }}
              >
                Add your business once, verify the account, then use the portal to manage
                each host location as your team grows.
              </MDTypography>

              <Grid container spacing={1.5} mt={2}>
                {steps.map((step, index) => (
                  <Grid item xs={12} sm={6} key={step}>
                    <MDBox
                      sx={{
                        p: 1.75,
                        minHeight: 88,
                        borderRadius: "15px",
                        backgroundColor: "rgba(255, 255, 255, 0.08)",
                        border: "1px solid rgba(255, 255, 255, 0.10)",
                        display: "flex",
                        gap: 1.25,
                        alignItems: "flex-start",
                      }}
                    >
                      <MDBox
                        sx={{
                          width: 30,
                          height: 30,
                          minWidth: 30,
                          borderRadius: "12px",
                          backgroundColor: "rgba(8, 120, 237, 0.22)",
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 900,
                        }}
                      >
                        {index + 1}
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="button" fontWeight="bold" sx={{ color: "#ffffff" }}>
                          {step}
                        </MDTypography>
                        <MDBox mt={0.5} display="flex" alignItems="center" gap={0.75}>
                          <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 15, color: "#6be334" }} />
                          <MDTypography
                            variant="caption"
                            sx={{ color: "rgba(248, 251, 255, 0.68)", fontWeight: 700 }}
                          >
                            Takes just a few minutes
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Grid>
                ))}
              </Grid>

              <MDBox mt={2.5} textAlign="center">
                <MDTypography
                  component={Link}
                  to="/authentication/learn-more"
                  variant="button"
                  fontWeight="bold"
                  sx={{
                    color: "#168dff",
                    textDecoration: "none",
                    "&:hover": {
                      color: "#ffffff",
                    },
                  }}
                >
                  Learn more about hosting with {APP_NAME}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </BasicLayoutNew>
  );
}

export default SignUp;
